import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, Phone } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import RelatedPosts from "@/components/RelatedPosts";
import PostClusterNav, { detectCluster } from "@/components/PostClusterNav";
import { useBooking } from "@/components/BookingDialog";
import { recordViewed } from "@/lib/recentlyViewed";
import {
  applySeo,
  buildBlogPostingSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  SITE_INFO,
} from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { openBooking } = useBooking();

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/blog/${slug}`);
        if (cancel) return;
        const data = res?.data;
        // If backend resolved an old alias slug, replace URL with the canonical one.
        if (data?.redirect_from && data?.slug && data.slug !== slug) {
          navigate(`/blog/${data.slug}`, { replace: true });
          return;
        }
        setPost(data);
      } catch (e) {
        if (!cancel) setNotFound(true);
      }
    })();
    return () => { cancel = true; };
  }, [slug, navigate]);

  useEffect(() => {
    if (!post) return;
    const ld = [
      buildBlogPostingSchema(post),
      buildBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: post.title, url: `/blog/${post.slug}` },
      ]),
    ];
    if (post.faq?.length) ld.push(buildFaqSchema(post.faq));
    applySeo({
      title: `${post.title} | Beauty & Balance by Bella`,
      description: post.meta_description || post.excerpt,
      path: `/blog/${post.slug}`,
      image: post.hero_image,
      type: "article",
      jsonLd: ld,
    });
    recordViewed(post);
  }, [post]);

  if (notFound) {
    return (
      <main className="bg-[#FAF6E8] min-h-screen">
        <Navigation />
        <section className="pt-40 pb-32 text-center px-6">
          <h1 className="font-serif text-4xl bb-ink mb-4">Article not found</h1>
          <Link to="/blog" className="text-link-hover bb-ink underline">
            Back to the blog
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  if (!post) {
    return (
      <main className="bg-[#FAF6E8] min-h-screen">
        <Navigation />
        <section className="pt-40 pb-32 text-center bb-ink-soft">Loading…</section>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid="blog-post-page" className="relative bg-[#FAF6E8] min-h-screen">
      <Navigation />

      <article className="pt-32 md:pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-8">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-sm bb-ink-soft link-underline text-link-hover mb-10"
          >
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>

          <div className="flex items-center gap-4 mb-5 overline">
            <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> {post.read_minutes} min read
            </span>
          </div>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light bb-ink mb-6">
            {post.title}
          </h1>
          <p className="bb-ink-soft text-lg leading-relaxed font-light mb-10">
            {post.excerpt}
          </p>

          {post.hero_image && (
            <img
              src={post.hero_image}
              alt={post.title}
              className="w-full h-[300px] md:h-[440px] object-cover rounded-lg border border-[#D9CFA0] mb-12"
            />
          )}

          <div
            data-testid="blog-post-content"
            className="bb-prose font-light"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
          />

          {(() => {
            const clusterKey = detectCluster(post.slug, post.tags);
            return clusterKey ? (
              <PostClusterNav currentSlug={post.slug} clusterKey={clusterKey} />
            ) : null;
          })()}

          {post.faq?.length > 0 && (
            <section className="mt-16" data-testid="blog-faq">
              <h2 className="font-serif text-3xl md:text-4xl bb-ink font-light mb-8">
                Frequently asked
              </h2>
              <ul className="space-y-6">
                {post.faq.map((f, i) => (
                  <li key={i} className="border-t border-[#D9CFA0] pt-5">
                    <h3 className="font-serif text-xl bb-ink mb-2">{f.q}</h3>
                    <p className="bb-ink-soft text-base leading-relaxed font-light">
                      {f.a}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Local-business call-out for AEO/GEO grounding */}
          <aside className="mt-16 bg-[#F2EBD3] border border-[#D9CFA0] rounded-lg p-6 md:p-8">
            <div className="overline mb-3">About Beauty &amp; Balance</div>
            <p className="bb-ink text-base font-light leading-relaxed">
              <strong>Beauty &amp; Balance by Bella</strong> is a licensed
              esthetician studio in downtown {SITE_INFO.city}, {SITE_INFO.region}.
              Bella offers therapeutic massage and makeup, with custom facials
              launching soon. Reach the studio at{" "}
              <a href={`tel:${SITE_INFO.phone}`} className="link-underline text-link-hover">
                {SITE_INFO.phoneDisplay}
              </a>{" "}
              or{" "}
              <a href={`mailto:${SITE_INFO.email}`} className="link-underline text-link-hover">
                {SITE_INFO.email}
              </a>.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={() => openBooking()}
                data-testid="blog-book-now"
                className="btn-shine inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm tracking-wider uppercase text-[#0F1F4E] bg-[#EAD584] hover:bg-[#F0DC8A] transition-colors"
              >
                Book a session
              </button>
              <a
                href={`tel:${SITE_INFO.phone}`}
                className="inline-flex items-center gap-2 text-sm bb-ink link-underline text-link-hover"
              >
                <Phone className="w-4 h-4" /> {SITE_INFO.phoneDisplay}
              </a>
            </div>
          </aside>

          <RelatedPosts currentSlug={post.slug} currentTags={post.tags || []} />
        </div>
      </article>

      <Footer />
    </main>
  );
}
